import { useEffect, useState } from 'react'
import moment from 'moment'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const EVENT_DATE = moment('2026-05-16T10:00:00+10:00')

interface TimeLeft {
  days: number
  hours: number
  minutes: number
  seconds: number
}

function calculateTimeLeft(): TimeLeft {
  const now = moment()
  const diff = EVENT_DATE.diff(now)

  if (diff <= 0) {
    return { days: 0, hours: 0, minutes: 0, seconds: 0 }
  }

  const duration = moment.duration(diff)
  return {
    days: Math.floor(duration.asDays()),
    hours: duration.hours(),
    minutes: duration.minutes(),
    seconds: duration.seconds(),
  }
}

const CountdownContainer = styled(Group)`
  position: relative;
  z-index: 2;
  margin-top: ${rem(-30)};
  margin-bottom: ${rem(-30)};
  padding: 0 ${rem(20)};

  ${mediaQueryUp.desktop} {
    margin-top: ${rem(-40)};
    margin-bottom: ${rem(-40)};
  }
`

const CountdownNumber = styled(Heading)`
  && {
    font-size: ${rem(18)};
  }

  ${mediaQueryUp.desktop} {
    && {
      font-size: ${rem(32)};
    }
  }
`

const CountdownPrefix = styled(Heading)`
  && {
    font-family: ${props => props.theme.font.secondaryAlternate.family};
    font-weight: 400;
    font-size: ${rem(14)};
    letter-spacing: 0.5px;
  }

  ${mediaQueryUp.desktop} {
    && {
      font-size: ${rem(20)};
    }
  }
`

function LeLiveCountdown() {
  const intl = useIntl()
  const [timeLeft, setTimeLeft] = useState<TimeLeft>(calculateTimeLeft)

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => {
      clearInterval(timer)
    }
  }, [])

  const { days, hours, minutes, seconds } = timeLeft
  const isOver = days === 0 && hours === 0 && minutes === 0 && seconds === 0

  if (isOver) {
    return null
  }

  return (
    <CountdownContainer
      direction="horizontal"
      horizontalAlign="center"
    >
      <Sheet
        background="neutral-eight"
        shadow="bottom-large"
        borderRadius="round"
        padding="8 16"
        desktopPadding="12 40"
      >
        <Group direction="horizontal" gap={8} desktopGap={16} verticalAlign="center">
          <CSSBreakpoint max="tablet">
            <CountdownPrefix variant="heading3alt" as="span" colour="neutral-one">
              {intl.formatMessage({
                id: 'NLD5jQLpj2nAsphthoL-',
                defaultMessage: 'See you in',
                description: 'Introductory text shown before the countdown timer on mobile devices on the LE Live event page',
              })}
            </CountdownPrefix>
          </CSSBreakpoint>
          <CSSBreakpoint min="desktop">
            <CountdownPrefix variant="heading3alt" as="span" colour="neutral-one">
              {intl.formatMessage({
                id: 'QBOwiw5rdOlLLICL0Pak',
                defaultMessage: 'See you at Luxury Escapes Live in',
                description: 'Introductory text shown before the countdown timer on desktop screens on the LE Live event page',
              })}
            </CountdownPrefix>
          </CSSBreakpoint>
          <Group direction="horizontal" gap={8} desktopGap={16} verticalAlign="center">
            <Group direction="vertical" gap={4} horizontalAlign="center">
              <CountdownNumber variant="heading2" as="span" colour="neutral-one">
                {String(days).padStart(2, '0')}
              </CountdownNumber>
              <BodyText variant="small" colour="neutral-three" format="uppercase">
                {intl.formatMessage({
                  id: '7KQCiiQQV-8LmwxRs_Dh',
                  defaultMessage: 'Days',
                  description: 'Unit label for days displayed below the number in the countdown timer on the LE Live event page',
                })}
              </BodyText>
            </Group>
            <Group direction="vertical" gap={4} horizontalAlign="center">
              <CountdownNumber variant="heading2" as="span" colour="neutral-one">
                {String(hours).padStart(2, '0')}
              </CountdownNumber>
              <BodyText variant="small" colour="neutral-three" format="uppercase">
                {intl.formatMessage({
                  id: 'HhvERCon4k6erM_kMoUy',
                  defaultMessage: 'Hours',
                  description: 'Unit label for hours displayed below the number in the countdown timer on the LE Live event page',
                })}
              </BodyText>
            </Group>
            <Group direction="vertical" gap={4} horizontalAlign="center">
              <CountdownNumber variant="heading2" as="span" colour="neutral-one">
                {String(minutes).padStart(2, '0')}
              </CountdownNumber>
              <BodyText variant="small" colour="neutral-three" format="uppercase">
                {intl.formatMessage({
                  id: '5upzTciUby5ydqtL0SLJ',
                  defaultMessage: 'Minutes',
                  description: 'Unit label for minutes displayed below the number in the countdown timer on the LE Live event page',
                })}
              </BodyText>
            </Group>
            <Group direction="vertical" gap={4} horizontalAlign="center">
              <CountdownNumber variant="heading2" as="span" colour="neutral-one">
                {String(seconds).padStart(2, '0')}
              </CountdownNumber>
              <BodyText variant="small" colour="neutral-three" format="uppercase">
                {intl.formatMessage({
                  id: 'C4f8vafR7nGQseRUegYN',
                  defaultMessage: 'Seconds',
                  description: 'Unit label for seconds displayed below the number in the countdown timer on the LE Live event page',
                })}
              </BodyText>
            </Group>
          </Group>
        </Group>
      </Sheet>
    </CountdownContainer>
  )
}

export default LeLiveCountdown
